import { MenuItem } from "@material-ui/core";
import { CalendarToday } from "@material-ui/icons";
import { DateTime } from "luxon";
import React from "react";
import { DEFAULT_WINDOW_SIZE_IN_DAYS } from "../consts";
import RangeSelect from "../DateRangePicker";
import RollingWindowSelect from "../DaysWindowPicker";
import { DateTimeString } from "../setup-scheduling-link";
import SchedulingSelect from "../SetupLinkSelect";
import { PeriodType } from "./../../../__generated__/globalTypes";

export type TimeRange =
  | {
      endDate: DateTimeString;
      periodType: PeriodType.Range;
      startDate: DateTimeString;
      windowSizeDays: null;
    }
  | {
      endDate: null;
      periodType: PeriodType.RollingWindow;
      startDate: null;
      windowSizeDays: number;
    };

export type TimeRangePickerProps = {
  endDate: DateTimeString | null;
  onChange: (timeRangeUpdate: TimeRange) => void;
  periodType: PeriodType;
  startDate: DateTimeString | null;
  windowSizeDays: number | null;
};

export type TimeRangeAction =
  | {
      type: "change-periodType";
      delta: {
        periodType: PeriodType;
      };
    }
  | {
      type: "change-dateRange";
      delta: {
        startDate: DateTimeString;
        endDate: DateTimeString;
      };
    }
  | {
      type: "change-windowSizeDays";
      delta: {
        windowSizeDays: number;
      };
    };

/** return time range with null set according to period type; populates related fields with defaults if required */
const getTimeRange = ({
  endDate,
  periodType,
  startDate,
  windowSizeDays,
}: {
  endDate: DateTimeString | null;
  periodType: PeriodType;
  startDate: DateTimeString | null;
  windowSizeDays: number | null;
}) => {
  if (periodType === PeriodType.Range) {
    return {
      endDate:
        endDate || DateTime.now().plus({ days: DEFAULT_WINDOW_SIZE_IN_DAYS }).endOf("day").toISO(),
      periodType: PeriodType.Range,
      startDate: startDate || DateTime.now().startOf("day").toISO(),
      windowSizeDays: null,
    } as TimeRange;
  } else {
    return {
      endDate: null,
      periodType: PeriodType.RollingWindow,
      startDate: null,
      windowSizeDays: windowSizeDays || DEFAULT_WINDOW_SIZE_IN_DAYS,
    } as TimeRange;
  }
};

const TimeRangePicker = ({
  endDate,
  onChange,
  periodType,
  startDate,
  windowSizeDays,
}: TimeRangePickerProps) => {
  const currentTimeRange = getTimeRange({ endDate, periodType, startDate, windowSizeDays });

  const handleTimeRangeChange = (event: React.ChangeEvent<{ value: PeriodType }>) =>
    onChange(
      getTimeRange({
        ...currentTimeRange,
        periodType: event.target.value,
      }),
    );

  const handleDaysChange = (windowSizeDays: number) =>
    onChange(
      getTimeRange({
        ...currentTimeRange,
        periodType: PeriodType.RollingWindow,
        windowSizeDays,
      }),
    );

  const handleDateRangeChange = (startDate: string, endDate: string) =>
    onChange(
      getTimeRange({
        ...currentTimeRange,
        endDate,
        periodType: PeriodType.Range,
        startDate,
      }),
    );

  return (
    <>
      <CalendarToday />
      <SchedulingSelect
        cw-id="links-time-window-select"
        id="links-time-window-select"
        value={periodType}
        onChange={handleTimeRangeChange}
      >
        <MenuItem value={PeriodType.RollingWindow}>Within Next</MenuItem>
        <MenuItem value={PeriodType.Range}>From</MenuItem>
      </SchedulingSelect>
      {currentTimeRange.periodType === PeriodType.RollingWindow && (
        <RollingWindowSelect value={currentTimeRange.windowSizeDays} onChange={handleDaysChange} />
      )}
      {currentTimeRange.periodType === PeriodType.Range && (
        <RangeSelect
          endDate={currentTimeRange.endDate}
          startDate={currentTimeRange.startDate}
          onChange={handleDateRangeChange}
        />
      )}
    </>
  );
};

export default TimeRangePicker;
